import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, Area, AreaChart 
} from 'recharts';
import { 
  TrendingUp, Download, Calendar, Trophy, Target, 
  ChevronDown, ChevronUp, FileText, ArrowLeft 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerAnalytics = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [months, setMonths] = useState(6);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAllStats, setShowAllStats] = useState(false);
  const [posIdx, setPosIdx] = useState(0);

  useEffect(() => {
    fetchAnalytics();
  }, [months]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/player/analytics?months=${months}`);
      setAnalytics(response.data);
      setPosIdx(0);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/player/stats/export?format=csv&months=12`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `game_stats_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Stats exported successfully!');
    } catch (error) {
      toast.error('Failed to export stats');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportJSON = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/player/stats/export?format=json&months=12`);
      
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `game_stats_export.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Stats exported successfully!');
    } catch (error) {
      toast.error('Failed to export stats');
    } finally {
      setExportLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-slate-400';
    if (grade.startsWith('A')) return 'text-green-400';
    if (grade.startsWith('B')) return 'text-blue-400';
    if (grade.startsWith('C')) return 'text-yellow-400';
    return 'text-red-400';
  };

  const formatStatKey = (key, labels) => {
    return (labels || analytics?.stat_labels)?.[key] || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 flex items-center justify-center">
        <div className="text-white">Loading analytics...</div>
      </div>
    );
  }

  if (!analytics || analytics.total_games === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" className="text-slate-400 hover:text-white mb-4" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Card className="bg-slate-800/90 border-slate-700">
            <CardContent className="py-12 text-center">
              <Trophy className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Game Data Yet</h3>
              <p className="text-slate-400">
                Start tracking your game stats to see analytics here
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Multi-position athletes can switch between the positions they play.
  const positions = analytics.positions || [];
  const view = positions[posIdx] || {
    aggregate_stats: analytics.aggregate_stats,
    trends: analytics.trends,
    stat_labels: analytics.stat_labels,
    position: analytics.primary_position,
    games: analytics.primary_position_games,
  };

  // Prepare chart data
  const trendData = (view.trends || []).map((t, idx) => ({
    ...t,
    gameNum: idx + 1,
    shortDate: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  // Get primary stat keys for charts based on position
  const chartStatKeys = Object.keys(view.aggregate_stats || {}).slice(0, 4);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" className="text-slate-400 hover:text-white" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          
          <div className="flex items-center gap-2">
            <Select value={months.toString()} onValueChange={(v) => setMonths(parseInt(v))}>
              <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="3" className="text-white">3 Months</SelectItem>
                <SelectItem value="6" className="text-white">6 Months</SelectItem>
                <SelectItem value="12" className="text-white">12 Months</SelectItem>
                <SelectItem value="24" className="text-white">24 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Position switcher for multi-position athletes */}
        {positions.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="analytics-position-tabs">
            {positions.map((p, i) => (
              <button
                key={`${p.sport}-${p.position}`}
                onClick={() => setPosIdx(i)}
                data-testid={`analytics-position-tab-${p.position}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${
                  i === posIdx
                    ? 'bg-amber-500 text-slate-900'
                    : 'bg-slate-700/60 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {p.position?.replace(/_/g, ' ')}
                <span className={`ml-1.5 ${i === posIdx ? 'text-slate-800' : 'text-slate-400'}`}>
                  ({p.games})
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Overview Card */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              Performance Overview
            </CardTitle>
            <CardDescription className="text-slate-400">
              Last {months} months • {analytics.total_games} games
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-white">{analytics.total_games}</div>
                <div className="text-sm text-slate-400">Games Played</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-white">
                  {analytics.positions_played.length}
                </div>
                <div className="text-sm text-slate-400">Positions</div>
              </div>
              {analytics.recent_grades.length > 0 && (
                <>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <div className={`text-3xl font-bold ${getGradeColor(analytics.recent_grades[analytics.recent_grades.length - 1]?.grade)}`}>
                      {analytics.recent_grades[analytics.recent_grades.length - 1]?.grade || 'N/A'}
                    </div>
                    <div className="text-sm text-slate-400">Latest Grade</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-amber-400">
                      {analytics.recent_grades.filter(g => g.grade?.startsWith('A')).length}
                    </div>
                    <div className="text-sm text-slate-400">A Grades</div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Performance Trend Chart */}
        {trendData.length > 1 && (
          <Card className="bg-slate-800/90 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">Performance Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="colorStat1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="shortDate" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #374151',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#f59e0b' }}
                    />
                    <Legend />
                    {chartStatKeys.slice(0, 2).map((key, idx) => (
                      <Area 
                        key={key}
                        type="monotone" 
                        dataKey={key} 
                        stroke={idx === 0 ? '#f59e0b' : '#3b82f6'}
                        fill={idx === 0 ? 'url(#colorStat1)' : 'none'}
                        name={formatStatKey(key, view.stat_labels)}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Aggregate Stats */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-400" />
                Aggregate Statistics
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-slate-400"
                onClick={() => setShowAllStats(!showAllStats)}
              >
                {showAllStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showAllStats ? 'Show Less' : 'Show All'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(view.aggregate_stats || {})
                .slice(0, showAllStats ? undefined : 6)
                .map(([key, data]) => (
                  <div key={key} className="bg-slate-700/50 rounded-lg p-3">
                    <div className="text-sm text-slate-400 mb-1">{formatStatKey(key, view.stat_labels)}</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-white">{data.total}</span>
                      <span className="text-sm text-slate-400">total</span>
                    </div>
                    <div className="text-xs text-amber-400 mt-1">
                      {data.average} avg/game
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Games */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-400" />
              Recent Games
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.recent_grades.slice().reverse().map((game) => (
                <div 
                  key={game.session_id || `${game.opponent}-${game.date}`} 
                  className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                >
                  <div>
                    <div className="font-medium text-white">vs. {game.opponent}</div>
                    <div className="text-sm text-slate-400">{game.date}</div>
                  </div>
                  <Badge className={`${
                    game.grade?.startsWith('A') ? 'bg-green-500' :
                    game.grade?.startsWith('B') ? 'bg-blue-500' :
                    'bg-yellow-500'
                  } text-white`}>
                    {game.grade || 'N/A'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Export Section */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-purple-400" />
              Export Stats
            </CardTitle>
            <CardDescription className="text-slate-400">
              Download your complete game log for recruiters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={handleExportCSV}
                disabled={exportLoading}
                data-testid="export-csv-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={handleExportJSON}
                disabled={exportLoading}
                data-testid="export-json-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlayerAnalytics;
